import { ANIMATION_FPS, MAX_ANIMATION_SECONDS } from '../domain/schemas';
import type { Command, ParseError } from './commands';

/**
 * Deterministic grammar for keyframe animation (Phase 3B). Commands act on explicit
 * context: the animation selected on the timeline ("the current animation"), the object
 * or character selected on the canvas, and the playhead frame. This is a fixed set of
 * phrasings, not open-ended text-to-animation.
 */
type Result = { command: Command } | { error: ParseError } | null;

const err = (segment: string, message: string, example: string): Result => ({ error: { kind: 'invalid-value', message, segment, suggestions: [example] } });

const OBJ = String.raw`(?:the |this |that )?(?:selected |current )?(?:object|shape|item|selection|character|hero|it)`;
const ANIM = String.raw`(?:the |this |that )?(?:current |selected )?animation`;
const NUM = String.raw`(\d+(?:\.\d+)?)`;
const SECONDS = String.raw`${NUM}[ -]?(?:s|sec|secs|second|seconds)`;

const PRESET_WORDS: [RegExp, 'idle' | 'walk' | 'run' | 'jump' | 'float' | 'spin'][] = [
  [/^(walk|walking|walk in place)$/, 'walk'],
  [/^(run|running|run in place|jog|jogging)$/, 'run'],
  [/^(jump|jumping|hop|hopping|leap)$/, 'jump'],
  [/^(float|floating|hover|hovering|bob)$/, 'float'],
  [/^(spin|spinning|turn around|rotate continuously)$/, 'spin'],
  [/^(idle|breathe|breathing|stand idle|idle breathing)$/, 'idle'],
];

export function parseAnimationSegment(segment: string): Result {
  let m: RegExpMatchArray | null;

  // ---- Create
  if ((m = segment.match(new RegExp(`^(?:create|make|new|add) (?:a |an )?(?:new )?(?:${SECONDS} )?(?:long )?animation(?: (?:that is |lasting |of |for )?${SECONDS}(?: long)?)?(?: at ${NUM} ?fps)?$`)))) {
    const seconds = Number(m[1] ?? m[2] ?? 4);
    const fps = m[3] !== undefined ? Number(m[3]) : undefined;
    if (!(seconds > 0 && seconds <= MAX_ANIMATION_SECONDS)) return err(segment, `An animation can last 0–${MAX_ANIMATION_SECONDS} seconds.`, 'create a 4-second animation');
    if (fps !== undefined && !(ANIMATION_FPS as readonly number[]).includes(fps)) return err(segment, `Frame rate must be ${ANIMATION_FPS.join(', ')} fps.`, 'create a 4-second animation at 24 fps');
    return { command: { type: 'anim.create', seconds, ...(fps !== undefined ? { fps: fps as 12 | 24 | 30 | 60 } : {}) } };
  }

  // ---- Settings of the current animation
  if ((m = segment.match(new RegExp(`^(?:set|change) (?:the )?(?:${ANIM} )?(?:fps|frame ?rate)(?: of ${ANIM})? (?:to |= ?)?${NUM}(?: ?fps)?$`)))) {
    const fps = Number(m[1]);
    if (!(ANIMATION_FPS as readonly number[]).includes(fps)) return err(segment, `Frame rate must be ${ANIMATION_FPS.join(', ')} fps.`, 'set animation fps to 24');
    return { command: { type: 'anim.fps', fps: fps as 12 | 24 | 30 | 60 } };
  }
  if ((m = segment.match(new RegExp(`^(?:set|change|make) (?:the )?(?:${ANIM} )?(?:duration|length)(?: of ${ANIM})? (?:to |= ?)?${SECONDS}$`))) || (m = segment.match(new RegExp(`^make ${ANIM} ${SECONDS}(?: long)?$`)))) {
    const seconds = Number(m[1]);
    if (!(seconds > 0 && seconds <= MAX_ANIMATION_SECONDS)) return err(segment, `An animation can last 0–${MAX_ANIMATION_SECONDS} seconds.`, 'set the animation duration to 6 seconds');
    return { command: { type: 'anim.duration', seconds } };
  }
  if (new RegExp(`^(?:loop|repeat) ${ANIM}$`).test(segment) || new RegExp(`^make ${ANIM} loop$`).test(segment)) return { command: { type: 'anim.loop', mode: 'loop' } };
  if (new RegExp(`^(?:play ${ANIM} once|make ${ANIM} play once|(?:don't|do not|stop) loop(?:ing)? ${ANIM})$`).test(segment)) return { command: { type: 'anim.loop', mode: 'once' } };
  if (new RegExp(`^(?:ping-?pong ${ANIM}|make ${ANIM} ping-?pong)$`).test(segment)) return { command: { type: 'anim.loop', mode: 'pingpong' } };

  // ---- Transport ("pause the animation" stays the Phase 0 command)
  if (new RegExp(`^(?:restart|replay|play) ${ANIM}(?: from the (?:start|beginning))?(?: again)?$`).test(segment) && /restart|replay|again|beginning|start$/.test(segment)) {
    return { command: { type: 'animation.restart' } };
  }

  // ---- Keyframes at a frame
  if ((m = segment.match(/^(?:add|set|create|insert) (?:a |an )?(position|rotation|scale|size|opacity|transform)? ?key(?: ?frames?)? (?:at|on) frame (\d+)(?: for (?:the )?(?:selected |current )?(?:object|character|it))?$/))) {
    const kind = (m[1] === 'size' ? 'scale' : m[1] ?? 'transform') as 'position' | 'rotation' | 'scale' | 'opacity' | 'transform';
    return { command: { type: 'anim.key', what: kind, frame: Number(m[2]) } };
  }

  // ---- Motion over the animation
  if ((m = segment.match(new RegExp(`^(?:animate|move) ${OBJ} (?:moving |to move |going |walking |running )?(left|right|up|down)(?: by)?(?: ${NUM} ?(?:pixels?|px))?(?: over (?:the (?:whole )?animation|${SECONDS}))?$`)))) {
    const distance = m[2] !== undefined ? Number(m[2]) : undefined;
    if (distance !== undefined && distance > 20000) return err(segment, 'Move by at most 20 000 pixels.', 'animate the selected character moving right 300 pixels');
    if (!/^animate/.test(segment) && m[3] === undefined) return null; // "move it left" (no animation words) is a scene edit
    return { command: { type: 'anim.move', direction: m[1] as 'left' | 'right' | 'up' | 'down', ...(distance !== undefined ? { distance } : {}), ...(m[3] !== undefined ? { seconds: Number(m[3]) } : {}) } };
  }
  if ((m = segment.match(new RegExp(`^(?:rotate|spin|turn) ${OBJ} (?:by )?(-?${NUM.slice(1, -1)}) ?(?:degrees?|°)(?: (clockwise|counter-?clockwise|anticlockwise))? over ${SECONDS}$`)))) {
    const degrees = Number(m[1]) * (/counter|anti/.test(m[2] ?? '') ? -1 : 1);
    const seconds = Number(m[3]);
    if (Math.abs(degrees) > 36000) return err(segment, 'Rotate by at most 36 000 degrees.', 'rotate the selected object 90 degrees over 2 seconds');
    if (!(seconds > 0 && seconds <= MAX_ANIMATION_SECONDS)) return err(segment, `The duration must be 0–${MAX_ANIMATION_SECONDS} seconds.`, 'rotate the selected object 90 degrees over 2 seconds');
    return { command: { type: 'anim.rotate', degrees, seconds } };
  }

  // ---- Presets: "make the selected character walk", "make it float"
  if ((m = segment.match(new RegExp(`^(?:make|let|have) ${OBJ} (.+)$`)))) {
    const preset = PRESET_WORDS.find(([re]) => re.test(m![1]!.trim()))?.[1];
    if (preset) return { command: { type: 'anim.preset', preset } };
  }
  if ((m = segment.match(new RegExp(`^(?:apply|add|use) (?:the |a )?(walk|walking|run|running|jump|jumping|float|floating|spin|spinning|idle|idle breathing|breathing)(?: cycle| preset| animation)?(?: (?:to|on) ${OBJ})?$`)))) {
    const preset = PRESET_WORDS.find(([re]) => re.test(m![1]!))?.[1];
    if (preset) return { command: { type: 'anim.preset', preset } };
  }

  // Open-ended requests are explained, not guessed.
  if (/\b(animate|animation)\b/.test(segment) && /\b(danc\w*|fight\w*|talk\w*|lip ?sync\w*|realistic\w*|ai|generat\w*|story|cinematic\w*)\b/.test(segment)) {
    return {
      error: {
        kind: 'unsupported',
        message: 'Free-form text-to-animation is not available. Animations are built from keyframes and presets (idle, walk, run, jump, float, spin).',
        segment,
        suggestions: ['make the selected character walk', 'animate the selected character moving right', 'rotate the selected object 90 degrees over 2 seconds'],
      },
    };
  }
  return null;
}

export const isAnimCommand = (c: Command): boolean => c.type.startsWith('anim.');
